import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from '../../components/yoda/components/Toast/toast';
import {
  ApiError,
  ServerError,
  NetworkError,
  TimeoutError,
  CorsError,
} from '../utils/errors';
import { sentry } from '../utils/sentry';

// Helper function to determine if an error should be sent to Sentry
const shouldCaptureError = (error: unknown): boolean => {
  // Always capture server errors (5xx)
  if (error instanceof ServerError) {
    return true;
  }

  // Always capture network-related errors
  if (
    error instanceof NetworkError ||
    error instanceof TimeoutError ||
    error instanceof CorsError
  ) {
    return true;
  }

  // Don't capture expected client errors (4xx)
  if (error instanceof ApiError) {
    // 4xx errors are expected user errors
    return false;
  }

  // Capture any other unexpected errors
  return true;
};

// Create a client with global error handling for Sentry
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only capture errors that are unexpected (5xx, network issues, etc.)
      if (shouldCaptureError(error)) {
        sentry.captureError(error, {
          tags: {
            type: 'query',
            queryKey: JSON.stringify(query.queryKey),
          },
          extra: {
            queryHash: query.queryHash,
            state: query.state.status,
          },
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, _context, mutation) => {
      // Only capture errors that are unexpected (5xx, network issues, etc.)
      if (shouldCaptureError(error)) {
        sentry.captureError(error, {
          tags: {
            type: 'mutation',
            mutationKey: mutation.options.mutationKey
              ? JSON.stringify(mutation.options.mutationKey)
              : 'unknown',
          },
          extra: {
            variables,
            state: mutation.state.status,
          },
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours (renamed from cacheTime in v5)
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      onError: error => {
        // Show toast notification for mutation errors
        if (error instanceof ApiError) {
          toast.error(error.message);
        } else if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('An unexpected error occurred');
        }
      },
    },
  },
});
